import { HaftarahMelodyType, PronunciationType, SentenceGroupMelodyType, TagType, TorahMelodyType } from 'types/types'

import { filterVisibleTags } from '@/util/Tags'

const VoiceStyles: Array<string> = ['Reading 📖', 'Singing 🎵', 'Text']
const RecordingVoiceStyles: Array<string> = ['Reading 📖', 'Singing 🎵']
const READ = 0
const SING = 1
const TEXT = 2

type VoiceStyleSet = {
  voice: string
  pronunciation: PronunciationType | null
  torahMelody: TorahMelodyType | null
  haftarahMelody: HaftarahMelodyType | null
  sentenceGroupMelody: SentenceGroupMelodyType | null
  tags: Array<TagType> | null
}

function inTextMode(voiceStyleSet: VoiceStyleSet): boolean {
  return voiceStyleSet.voice === VoiceStyles[TEXT]
}

function inReadMode(voiceStyleSet: VoiceStyleSet): boolean {
  return voiceStyleSet.voice === VoiceStyles[READ]
}

function inSingMode(voiceStyleSet: VoiceStyleSet): boolean {
  return voiceStyleSet.voice === VoiceStyles[SING]
}

function hasMelody(obj: { torahMelody?: unknown; haftarahMelody?: unknown; sentenceGroupMelody?: unknown }): boolean {
  return !!(obj.torahMelody || obj.haftarahMelody || obj.sentenceGroupMelody)
}

const defaultVoiceStyleSet = (): VoiceStyleSet => {
  return {
    haftarahMelody: null,
    pronunciation: null,
    sentenceGroupMelody: null,
    tags: null,
    torahMelody: null,
    voice: VoiceStyles[SING],
  }
}

function voiceStyleSetFromRecording(recording: {
  pronunciation?: PronunciationType | null
  torahMelody?: TorahMelodyType | null
  haftarahMelody?: HaftarahMelodyType | null
  sentenceGroupMelody?: SentenceGroupMelodyType | null
  tags?: Array<TagType> | null
}): VoiceStyleSet {
  return {
    haftarahMelody: recording.haftarahMelody || null,
    pronunciation: recording.pronunciation || null,
    sentenceGroupMelody: recording.sentenceGroupMelody || null,
    tags: recording.tags ? filterVisibleTags(recording.tags) : null,
    torahMelody: recording.torahMelody || null,
    voice: hasMelody(recording) ? VoiceStyles[SING] : VoiceStyles[READ],
  }
}

/**
 * Whether the voiceStyleSet is sufficiently configured for text rendering.
 * When `requireStyle` is true, a pronunciation and (for singing) a melody must be set;
 * otherwise any non-empty voiceStyleSet is considered ready.
 */
function voiceStyleReady(voiceStyleSet: VoiceStyleSet, requireStyle = true): boolean {
  if ((requireStyle && !voiceStyleSet.pronunciation) || !voiceStyleSet.voice) {
    return false
  }
  if (requireStyle && inSingMode(voiceStyleSet) && !hasMelody(voiceStyleSet)) {
    return false
  }
  return true
}

function isReadyForRecording(voiceStyleSet: VoiceStyleSet): boolean {
  if (!voiceStyleSet.pronunciation) {
    return false
  }

  if (!voiceStyleSet.voice) {
    return false
  }

  if (!voiceStyleSet.tags?.length) {
    return false
  }

  if (inReadMode(voiceStyleSet)) {
    return true
  }

  return hasMelody(voiceStyleSet)
}

export {
  defaultVoiceStyleSet,
  hasMelody,
  inReadMode,
  inSingMode,
  inTextMode,
  isReadyForRecording,
  READ,
  RecordingVoiceStyles,
  SING,
  TEXT,
  voiceStyleReady,
  VoiceStyles,
  VoiceStyleSet,
  voiceStyleSetFromRecording,
}
